import { useEffect, useRef, useState } from 'react';
import type { RemoteTrack, Room } from 'livekit-client';
import * as api from './api.ts';
import { Label } from './ui.tsx';

/**
 * The live-stream tile on the Live command view — the dashboard's window onto
 * an event's stream room (evac-evidence 03.05; B2 at the pre-committed
 * cut-list scope: LiveKit defaults, one `interrupted`→catch-up shown live).
 *
 * Three honest states, and a fourth that is silence:
 *
 * - **Nothing at all** when the server answers 501 — the provider is `none`,
 *   which is today's shipped posture, and a tile saying "streaming is off"
 *   would be the product apologising for a feature nobody turned on.
 * - **"No stream is being published for this event"** when connected and the
 *   room is empty. Connected-but-empty and not-connected are different facts
 *   and must not share a sentence.
 * - **"Live stream"** only when a video track has actually arrived. The tile
 *   never claims live on a connection alone.
 * - **"Stream interrupted — reconnecting"** on track drop or bearer loss.
 *   The SDK auto-resubscribes when the publisher returns; a full disconnect
 *   re-mints the token (they expire in ten minutes) and rejoins — the
 *   catch-up path, so an interrupted stream comes back without a refresh.
 *
 * The SDK is imported dynamically so every dashboard load that never sees a
 * stream (today: all of them) never downloads a WebRTC stack.
 */

type Phase = 'probing' | 'off' | 'connecting' | 'live' | 'empty' | 'interrupted';

/**
 * Shape-checked rather than `instanceof ApiError`: the tile must not couple
 * to the error class's identity — test doubles of the API module (and any
 * future refactor of it) carry the status without the class.
 */
function is501(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    (err as { status: unknown }).status === 501
  );
}

const STATUS: Record<Exclude<Phase, 'probing' | 'off'>, string> = {
  connecting: 'Connecting to the stream…',
  live: 'Live stream',
  empty: 'No stream is being published for this event',
  interrupted: 'Stream interrupted — reconnecting',
};

export function StreamTile({ orgId, eventId }: { orgId: string; eventId: string }) {
  const [phase, setPhase] = useState<Phase>('probing');
  // Bumped on a full disconnect: re-runs the effect, which mints a fresh
  // token (the old one may have expired during the outage) and rejoins.
  const [attempt, setAttempt] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let room: Room | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let settleAfterDrop: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      let grant: api.StreamToken | null;
      try {
        grant = await api.getStreamToken(orgId, eventId);
      } catch (err) {
        // 501 is "the provider is none" — render nothing, today's behaviour
        // unchanged. Any other failure also renders nothing rather than a
        // broken tile on the one screen that must stay calm; the next
        // attempt (if a disconnect scheduled one) will try again.
        if (!cancelled && !is501(err) && attempt > 0) {
          retry = setTimeout(() => setAttempt((a) => a + 1), 3_000);
        } else if (!cancelled) {
          setPhase('off');
        }
        return;
      }
      if (cancelled || !grant) {
        if (!cancelled) setPhase('off');
        return;
      }

      setPhase('connecting');
      const lk = await import('livekit-client');
      if (cancelled) return;

      const r = new lk.Room();
      room = r;
      const tracks = new Set<RemoteTrack>();
      const settle = () => {
        if (!cancelled) setPhase(tracks.size > 0 ? 'live' : 'empty');
      };

      r.on(lk.RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind === 'video') {
          tracks.add(track);
          if (videoRef.current) track.attach(videoRef.current);
        }
        clearTimeout(settleAfterDrop);
        settle();
      });
      r.on(lk.RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        tracks.delete(track);
        if (cancelled) return;
        // Interrupted first, honestly: the publisher may be coming straight
        // back (the SDK resubscribes on its own), so the tile waits before
        // downgrading the sentence to "nothing is being published".
        setPhase('interrupted');
        clearTimeout(settleAfterDrop);
        settleAfterDrop = setTimeout(settle, 5_000);
      });
      r.on(lk.RoomEvent.Reconnecting, () => {
        if (!cancelled) setPhase('interrupted');
      });
      r.on(lk.RoomEvent.Reconnected, settle);
      r.on(lk.RoomEvent.Disconnected, () => {
        if (cancelled) return;
        setPhase('interrupted');
        // A fresh token and a fresh join, not a spinner: the ten-minute
        // token has likely outlived the outage that caused this.
        retry = setTimeout(() => setAttempt((a) => a + 1), 3_000);
      });

      try {
        await r.connect(grant.url, grant.token);
        settle();
      } catch {
        if (cancelled) return;
        setPhase('interrupted');
        retry = setTimeout(() => setAttempt((a) => a + 1), 3_000);
      }
    })().catch(() => {
      // Nothing above should escape, but a rejection out of an effect is an
      // unhandled promise — and the calm answer to the unexpected is the
      // same as to a 501: no tile.
      if (!cancelled) setPhase('off');
    });

    return () => {
      cancelled = true;
      clearTimeout(retry);
      clearTimeout(settleAfterDrop);
      void room?.disconnect();
    };
  }, [orgId, eventId, attempt]);

  if (phase === 'probing' || phase === 'off') return null;

  return (
    <div className="mt-8">
      <div className="mb-2">
        <Label>Live stream</Label>
      </div>
      <div
        className="relative overflow-hidden rounded-[8px] border border-[var(--line)] bg-black"
        style={{ aspectRatio: '16 / 9' }}
      >
        {/*
          Muted so autoplay is never blocked: a stream tile that renders a
          paused first frame behind a browser's play gate is a live view
          claiming something it is not showing. Live audio to the operator is
          an MC-console concern (03.05 §3.2), not this dashboard tile's.
        */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          aria-label="Live stream from this event"
          className="h-full w-full object-contain"
          style={{ display: phase === 'live' ? 'block' : 'none' }}
        />
        {/*
          The state in words — one element that overlays the tile while there
          is nothing to show and steps out of sight (never out of the DOM,
          which would silence the announcement) when video takes over.
          `role="status"`, not `alert`: a stream state is a passive live
          value, never news that interrupts.
        */}
        <p
          role="status"
          aria-live="polite"
          className={
            phase === 'live'
              ? 'sr-only'
              : 'absolute inset-0 flex items-center justify-center px-6 text-center text-[14px] text-[var(--ink-2)]'
          }
        >
          {STATUS[phase]}
        </p>
      </div>
    </div>
  );
}
